import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAuth } from "../../contexts/JWTContext";
import { betTypeLabel, money, money2, pushToast, useAsync, useCountdown } from "../lib";
import { Countdown, Empty, FlagBadge, Sheet, Skeleton } from "../ui";

const NEED = {
  "3T": 3, "3TOD": 3, "3B": 3, "3F": 3, "2T": 2, "2B": 2, RUN_T: 1, RUN_B: 1, "19DOOR": 1, RUD_F: 1, RUD_B: 1,
  "4D_BIG": 4, "4D_SMALL": 4,
};
const REVERSIBLE = new Set(["2T", "2B", "3T", "3B", "3F", "3TOD"]);
const QUICK_AMOUNTS = [5, 10, 20, 50, 100];
const SLIP_AUTO_COLLAPSE = 3;

// ตัวช่วยแทง (กลับเลข / 19 ประตู / รูดหน้า / รูดหลัง) ขยายเป็นรายการเลขจริงในโพยทันที ให้ลูกค้าเห็นว่าจะแทงเลขอะไรบ้าง
// และลบ/แก้ยอดทีละเลขได้ — ต้องตรงกับ expandLine ฝั่ง server (services/lottoNumbers.js)
const METHOD_LABEL = { REVERSE: "กลับเลข", "19DOOR": "19 ประตู", RUD_F: "รูดหน้า", RUD_B: "รูดหลัง" };
const permutations = (str) => {
  const out = new Set();
  const walk = (prefix, rest) => {
    if (!rest) return out.add(prefix);
    for (let i = 0; i < rest.length; i++) walk(prefix + rest[i], rest.slice(0, i) + rest.slice(i + 1));
    return null;
  };
  walk("", String(str));
  return [...out];
};
/** @returns {Array<{betType:string, number:string, method:string}>} betType เป็นรูปแบบจริงที่บันทึก (2T/2B/3T...) */
function expandEntry(betType, num, reverse) {
  const n = String(num);
  if (betType === "19DOOR") {
    const set = new Set();
    for (let i = 0; i <= 9; i++) {
      set.add(`${n}${i}`);
      set.add(`${i}${n}`);
    }
    return [...set].map((x) => ({ betType: "2T", number: x, method: "19DOOR" }));
  }
  if (betType === "RUD_F") return Array.from({ length: 10 }, (_, i) => ({ betType: "2T", number: `${n}${i}`, method: "RUD_F" }));
  if (betType === "RUD_B") return Array.from({ length: 10 }, (_, i) => ({ betType: "2B", number: `${i}${n}`, method: "RUD_B" }));
  if (reverse && REVERSIBLE.has(betType)) {
    // เลขที่กรอกขึ้นก่อน แล้วตามด้วยเลขที่สลับตำแหน่ง
    const rest = permutations(n).filter((x) => x !== n);
    return [n, ...rest].map((x) => ({ betType, number: x, method: "REVERSE" }));
  }
  return [{ betType, number: n, method: "DIRECT" }];
}
// 4D (Big/Small): เลขเดียวมีได้หลายชั้นรางวัล (1/2/3/พิเศษ/ปลอบใจ) คนละเรต
// เรตที่แสดงในหน้านี้คือเรตรางวัลที่ 1 (สูงสุด) เท่านั้น ไม่ใช่เรตจ่ายจริงเสมอไป
const isFourD = (code) => code === "4D_BIG" || code === "4D_SMALL";

// จัดกลุ่มรูปแบบแทงให้เลือกง่ายขึ้น — เลือกกลุ่มก่อน (3 ตัว / 2 ตัว / วิ่ง / 4D) แล้วค่อยกดขยายดูรูปแบบย่อยในกลุ่มนั้น
// แทนที่จะเรียงปนกันแถวเดียวยาว ๆ แบบเดิมซึ่งดูยาก
const BT_GROUPS = [
  { key: "3", label: "3 ตัว", icon: "solar:hashtag-circle-bold-duotone", types: ["3T", "3TOD", "3F", "3B"] },
  { key: "2", label: "2 ตัว", icon: "solar:hashtag-square-bold-duotone", types: ["2T", "2B", "19DOOR", "RUD_F", "RUD_B"] },
  { key: "run", label: "วิ่ง", icon: "solar:running-round-bold-duotone", types: ["RUN_T", "RUN_B"] },
  { key: "4d", label: "4D", icon: "solar:ticket-sale-bold-duotone", types: ["4D_BIG", "4D_SMALL"] },
];

export default function PlayPage({ yeekee, onDone }) {
  const { roundId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { data, loading, error } = useAsync(() => lottoApi.round(roundId), [roundId]);

  const round = data?.round;
  const rates = useMemo(() => data?.rates || [], [data]);
  const limits = useMemo(() => data?.limits || [], [data]);
  const clock = useCountdown(round?.close_at);

  const rateMap = useMemo(() => {
    const m = {};
    rates.forEach((r) => (m[r.bet_type_code] = Number(r.payout_rate)));
    return m;
  }, [rates]);

  // available bet type buttons (real rates + helper types if 2T/2B enabled)
  const betTypes = useMemo(() => {
    const list = rates.map((r) => r.bet_type_code);
    const out = [];
    ["3T", "3TOD", "3F", "3B", "2T", "2B", "RUN_T", "RUN_B", "4D_BIG", "4D_SMALL"].forEach(
      (c) => list.includes(c) && out.push(c),
    );
    if (list.includes("2T")) out.push("19DOOR", "RUD_F");
    if (list.includes("2B")) out.push("RUD_B");
    return out;
  }, [rates]);

  const [bt, setBt] = useState(null);
  useEffect(() => {
    if (!bt && betTypes.length) setBt(betTypes[0]);
  }, [betTypes, bt]);

  // กลุ่มที่มีให้เลือกจริง (ตัดกลุ่มที่ไม่มีรูปแบบรองรับในงวดนี้ออก)
  const btGroups = useMemo(
    () =>
      BT_GROUPS.map((g) => ({ ...g, types: g.types.filter((t) => betTypes.includes(t)) })).filter(
        (g) => g.types.length,
      ),
    [betTypes],
  );
  const activeGroup = btGroups.find((g) => g.types.includes(bt)) || btGroups[0];
  const selectGroup = (g) => {
    const first = g.types[0];
    if (first) {
      setBt(first);
      setEntry("");
    }
  };

  const [entry, setEntry] = useState("");
  const [typing, setTyping] = useState(false);
  const entryInput = useRef(null);
  const [reverse, setReverse] = useState(false);
  // แทงใหม่จากหน้าโพยของฉัน — มาพร้อม location.state.prefill ([{betType, number, amount}])
  const [cart, setCart] = useState(() => {
    const prefill = location.state?.prefill;
    if (!Array.isArray(prefill) || !prefill.length) return [];
    const seen = new Set();
    return prefill
      .filter((p) => {
        const k = `${p.betType}|${p.number}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .map((p) => ({ key: `${p.betType}|${p.number}`, betType: p.betType, number: p.number, method: "DIRECT", amount: p.amount }));
  }); // { key, betType(รูปแบบจริง), number, method: DIRECT|REVERSE|19DOOR|RUD_F|RUD_B, amount }
  const [defAmt, setDefAmt] = useState(10);
  // โพยยาวเกินไปจะบังปุ่มตัวเลข → พับเหลือแถบจำนวนรายการเองเมื่อมีเกิน SLIP_AUTO_COLLAPSE รายการ (กดขยาย/ย่อเองได้)
  const [slipOpen, setSlipOpen] = useState(() => cart.length <= SLIP_AUTO_COLLAPSE);
  const prevCartLen = useRef(cart.length);
  useEffect(() => {
    if (cart.length > prevCartLen.current && cart.length > SLIP_AUTO_COLLAPSE) setSlipOpen(false);
    if (cart.length === 0) setSlipOpen(true); // โพยว่าง → รอบหน้าเริ่มแบบกางอยู่
    prevCartLen.current = cart.length;
  }, [cart.length]);
  const [submitting, setSubmitting] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false); // มือถือ: ตรวจสอบโพยก่อนยืนยัน

  useEffect(() => {
    if (location.state?.prefill?.length) {
      pushToast(`เติมเลขจากโพยเดิม ${location.state.prefill.length} รายการให้แล้ว ตรวจสอบก่อนยืนยัน`, "ok", 4000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const need = NEED[bt] || 2;
  const closedNums = useMemo(() => {
    const s = new Set();
    limits.forEach((l) => l.mode === "CLOSED" && s.add(`${l.bet_type_code}|${l.number}`));
    return s;
  }, [limits]);

  // เลขอั้นจ่าย: จ่ายเป็น % ของเรตเต็ม
  const payLimitMap = useMemo(() => {
    const m = {};
    limits.forEach((l) => {
      if (l.mode === "RATE_OVERRIDE" && l.pay_percent != null)
        m[`${l.bet_type_code}|${l.number}`] = Number(l.pay_percent);
    });
    return m;
  }, [limits]);
  const payLimitOf = (code, num) => {
    const v = payLimitMap[`${code}|${num}`];
    return v != null ? v : null;
  };

  const addNumber = (num) => {
    const all = expandEntry(bt, num, reverse);
    const open = all.filter((x) => !closedNums.has(`${x.betType}|${x.number}`));
    const closedCount = all.length - open.length;
    if (!open.length) {
      pushToast(all.length > 1 ? "เลขชุดนี้ปิดรับทั้งหมด" : `เลข ${num} ปิดรับ`, "err");
      return;
    }
    if (closedCount) pushToast(`ข้าม ${closedCount} เลขที่ปิดรับ`, "err");
    const limited = open.filter((x) => payLimitOf(x.betType, x.number) != null);
    if (limited.length) {
      const pl = payLimitOf(limited[0].betType, limited[0].number);
      pushToast(
        limited.length === 1
          ? `เลข ${limited[0].number} เป็นเลขอั้นจ่าย — ถ้าถูกรางวัลจ่ายเพียง ${pl}% ของเรตปกติ`
          : `มี ${limited.length} เลขเป็นเลขอั้นจ่าย (เช่น ${limited[0].number} จ่ายเพียง ${pl}%) — ดูในโพย`,
        "err",
        5000,
      );
    }
    const fresh = open.filter((x) => !cart.some((c) => c.key === `${x.betType}|${x.number}`));
    setCart((c) => {
      const have = new Set(c.map((x) => x.key));
      const add = open
        .map((x) => ({ key: `${x.betType}|${x.number}`, betType: x.betType, number: x.number, method: x.method, amount: defAmt }))
        .filter((x) => !have.has(x.key));
      return add.length ? [...c, ...add] : c;
    });
    setEntry("");
    // แจ้งจำนวนที่เพิ่ม เฉพาะเมื่อตัวช่วยขยายเป็นหลายเลข (เลขเดียวเห็นในโพยอยู่แล้ว)
    if (all.length > 1) pushToast(fresh.length ? `เพิ่ม ${fresh.length} เลขลงโพย` : "เลขชุดนี้อยู่ในโพยแล้ว", "ok", 2500);
  };

  const press = (d) => {
    if (d === "C") return setEntry("");
    if (d === "DEL") return setEntry((e) => e.slice(0, -1));
    const next = (entry + d).slice(0, need);
    if (next.length === need) {
      addNumber(next);
    } else {
      setEntry(next);
    }
  };

  // พิมพ์จากแป้นมือถือ/คีย์บอร์ด — ครบหลักเมื่อไหร่เพิ่มลงโพยทันที (วางหลายเลขติดกันได้ เช่น "123456" = 12, 34, 56)
  const typeEntry = (raw) => {
    let digits = String(raw).replace(/\D/g, "");
    while (digits.length >= need) {
      addNumber(digits.slice(0, need));
      digits = digits.slice(need);
    }
    setEntry(digits);
  };

  const total = cart.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const setAmt = (key, v) =>
    setCart((c) => c.map((x) => (x.key === key ? { ...x, amount: v } : x)));
  const applyAll = () => setCart((c) => c.map((x) => ({ ...x, amount: defAmt })));

  const submit = async () => {
    if (!isAuthenticated) return nav("/auth");
    if (!cart.length) return;
    setSubmitting(true);
    try {
      const lines = cart.map((x) => ({
        betType: x.betType,
        numbers: [x.number],
        amount: Number(x.amount) || 0,
        // บอก server ว่าเลขนี้มาจากตัวช่วยไหน (เก็บใน input_method) — ตัวเลขแต่ละตัวขยายมาครบแล้วจึงไม่ส่ง reverse
        inputMethod: x.method && x.method !== "DIRECT" ? x.method : undefined,
      }));
      const res = await lottoApi.placeBet({ roundId: Number(roundId), lines });
      pushToast(`ส่งโพยสำเร็จ #${res.bet_ref} · ${money(res.total_amount)} บาท`, "ok", 5000);
      setCart([]);
      setSheetOpen(false);
      onDone?.();
      setTimeout(() => nav("/mybets"), 700);
    } catch (e) {
      pushToast(e?.response?.data?.message || "ส่งโพยไม่สำเร็จ", "err", 5000);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ marginTop: 18 }}><Skeleton h={120} /><Skeleton h={260} style={{ marginTop: 12 }} /></div>;
  if (error || !round)
    return (
      <div className="lx-card" style={{ marginTop: 18 }}>
        <Empty icon="solar:document-text-bold-duotone">
          ไม่พบงวดนี้
          <div style={{ marginTop: 12 }}>
            <button className="lx-btn" onClick={() => nav("/")}>
              กลับหน้าแรก
            </button>
          </div>
        </Empty>
      </div>
    );

  const locked = clock.done || round.status !== "OPEN";

  // รายการเลขในโพย + ยอดรวม + ปุ่มยืนยัน — ใช้ทั้งแผงโพยบนเดสก์ท็อปและ sheet ตรวจสอบบนมือถือ
  const slipRows = (
    <div className="lx-slip-list">
      {cart.map((x) => {
        const rate = rateMap[x.betType];
        const pl = payLimitOf(x.betType, x.number);
        const payout = rate ? Number(x.amount || 0) * rate * (pl != null ? pl / 100 : 1) : null;
        return (
          <div className="lx-slip-row" key={x.key}>
            <div className="lx-slip-numchip">{x.number}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="lx-slip-tag">
                {betTypeLabel(x.betType)}
                {METHOD_LABEL[x.method] ? ` (${METHOD_LABEL[x.method]})` : ""}
                {rate ? ` · จ่าย${isFourD(x.betType) ? "สูงสุด" : ""} ${rate}` : ""}
              </div>
              {pl != null && (
                <div className="lx-slip-payout" style={{ color: "#e67e22", fontWeight: 600 }}>
                  ⚠ เลขอั้นจ่าย — จ่ายเพียง {pl}% ของเรต
                </div>
              )}
              {payout != null && (
                <div className="lx-slip-payout">
                  {isFourD(x.betType) ? "ถูกรางวัลที่ 1 รับสูงสุด" : "ถูกรับ"} {money2(payout)} บาท
                </div>
              )}
            </div>
            <span className="lx-amt-field">
              <input
                className="lx-amt-input"
                inputMode="numeric"
                value={x.amount}
                onChange={(e) => setAmt(x.key, e.target.value.replace(/\D/g, ""))}
              />
            </span>
            <button className="lx-x" onClick={() => setCart((c) => c.filter((i) => i.key !== x.key))}>
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
  const checkout = (
    <div className="lx-checkout">
      <div className="lx-spread" style={{ marginBottom: 12 }}>
        <span className="lx-muted">ยอดรวม</span>
        <b className="lx-mono" style={{ fontSize: 22 }}>
          {money2(total)} บาท
        </b>
      </div>
      <button className="lx-btn primary block" disabled={submitting || total <= 0} onClick={submit}>
        {submitting ? "กำลังส่ง..." : isAuthenticated ? "ยืนยันแทงหวย" : "เข้าสู่ระบบเพื่อแทง"}
      </button>
    </div>
  );

  return (
    <div className="lx-play vx-play" style={{ marginTop: 18 }}>
      <div className="lx-card vx-play-head">
        <div className="vx-ph-top">
          <FlagBadge code={round.type_code} category={round.category} size={54} />
          <div className="vx-ph-id">
            <div className="lx-h2">{round.type_name}</div>
            <div className="lx-muted vx-ph-round">
              <Icon icon="solar:ticket-bold-duotone" width={13} height={13} />
              {round.round_label}
            </div>
          </div>
        </div>
        <div className="vx-ph-clock">
          <span className="vx-ph-clock-label">
            <Icon icon="solar:alarm-bold-duotone" width={16} height={16} />
            ปิดรับใน
          </span>
          <Countdown clock={clock} />
        </div>
        {yeekee && (
          <div className="vx-ph-note">
            <Icon icon="solar:bolt-bold" width={13} height={13} />
            ยี่กีออกผลอัตโนมัติทันทีที่ปิดรับ
          </div>
        )}
      </div>

      {locked ? (
        <div className="lx-card">
          <Empty icon="solar:lock-keyhole-minimalistic-bold-duotone">งวดนี้ปิดรับแล้ว</Empty>
        </div>
      ) : (
        <>
          {/* bet type — เลือกกลุ่มก่อน แล้วเลือกรูปแบบย่อยพร้อมเรตจ่าย */}
          <section className="vx-pcard">
            <div className="vx-pcard-h">
              <span className="vx-pcard-ic">
                <Icon icon="solar:widget-5-bold-duotone" width={18} height={18} />
              </span>
              เลือกรูปแบบแทง
            </div>
            <div className="vx-groups">
              {btGroups.map((g) => (
                <button key={g.key} className={activeGroup?.key === g.key ? "on" : ""} onClick={() => selectGroup(g)}>
                  <Icon icon={g.icon} width={22} height={22} />
                  {g.label}
                </button>
              ))}
            </div>

            {activeGroup && activeGroup.types.length > 1 && (
              <div className="vx-rates">
                {activeGroup.types.map((c) => (
                  <button
                    key={c}
                    className={c === bt ? "on" : ""}
                    onClick={() => {
                      setBt(c);
                      setEntry("");
                    }}
                  >
                    <span className="n">{betTypeLabel(c)}</span>
                    {rateMap[c] && !c.includes("DOOR") && !c.startsWith("RUD") ? (
                      <span className="r">
                        {isFourD(c) ? "สูงสุด " : "จ่าย "}
                        <b>{rateMap[c]}</b>
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* default amount — ตั้งก่อนกรอกเลข เลขที่เพิ่มต่อจากนี้จะใช้ยอดนี้ */}
          <section className="vx-pcard">
            <div className="vx-pcard-h">
              <span className="vx-pcard-ic gold">
                <Icon icon="solar:wallet-money-bold-duotone" width={18} height={18} />
              </span>
              ยอดแทงต่อเลข
              <span className="lx-amt-field vx-pcard-end">
                <input
                  className="lx-amt-input"
                  aria-label="ยอดแทงต่อเลข"
                  inputMode="numeric"
                  value={defAmt}
                  onChange={(e) => setDefAmt(e.target.value.replace(/\D/g, ""))}
                />
              </span>
            </div>
            <div className="vx-amt-chips">
              {QUICK_AMOUNTS.map((a) => (
                <button key={a} className={`lx-tool ${String(defAmt) === String(a) ? "on" : ""}`} onClick={() => setDefAmt(a)}>
                  {a}
                </button>
              ))}
              <button className="lx-tool vx-amt-all" onClick={applyAll} disabled={!cart.length}>
                ใช้กับทุกเลขในโพย
              </button>
            </div>
            {rateMap[bt] && Number(defAmt) > 0 && (
              <div className="vx-amt-hint">
                <Icon icon="fluent-emoji-flat:money-bag" width={18} height={18} />
                <span>
                  {isFourD(bt) ? "ถูกรางวัลที่ 1 รับสูงสุด" : "ถูกแล้วรับ"} <b>{money2(Number(defAmt) * rateMap[bt])}</b> บาท/เลข
                  <small> ({defAmt} × {rateMap[bt]})</small>
                </span>
              </div>
            )}
          </section>

          {/* กรอกเลข */}
          <section className="vx-pcard vx-entrycard">
            <div className="vx-pcard-h">
              <span className="vx-pcard-ic">
                <Icon icon="solar:hashtag-square-bold-duotone" width={18} height={18} />
              </span>
              กรอกเลข
              <span className="vx-pcard-end vx-pill-bt">{betTypeLabel(bt)}</span>
            </div>
            <div className={`lx-entry ${typing ? "typing" : ""}`} onClick={() => entryInput.current?.focus()}>
              <input
                ref={entryInput}
                className="lx-entry-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                enterKeyHint="done"
                aria-label="พิมพ์เลขที่จะแทง"
                value={entry}
                onChange={(e) => typeEntry(e.target.value)}
                onFocus={() => setTyping(true)}
                onBlur={() => setTyping(false)}
              />
              {Array.from({ length: need }).map((_, i) => {
                const filled = Boolean(entry[i]);
                const isNext = !filled && i === entry.length;
                return (
                  <div key={i} className={`lx-entry-box ${filled ? "filled" : ""} ${isNext ? "next" : ""}`}>
                    {filled ? entry[i] : <span className="ph">•</span>}
                  </div>
                );
              })}
            </div>
            <div className="lx-entry-hint">
              <Icon icon="solar:keyboard-bold-duotone" width={14} height={14} />
              {typing ? "พิมพ์เลขได้เลย ครบหลักแล้วเพิ่มลงโพยอัตโนมัติ" : "แตะช่องตัวเลขเพื่อพิมพ์ด้วยแป้นมือถือ หรือกดปุ่มด้านล่าง"}
            </div>

            {REVERSIBLE.has(bt) && (
              <div className="lx-tools">
                <button className={`lx-tool ${reverse ? "on" : ""}`} onClick={() => setReverse((v) => !v)}>
                  <Icon icon="solar:shuffle-bold" width={13} height={13} style={{ verticalAlign: -2, marginRight: 4 }} />
                  กลับเลข {reverse ? "เปิด" : "ปิด"}
                </button>
                <span className="lx-muted" style={{ fontSize: 12, alignSelf: "center" }}>
                  เพิ่มทุกการสลับตำแหน่งอัตโนมัติ
                </span>
              </div>
            )}

            <div className="lx-pad">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
                <button key={d} className="lx-key" onClick={() => press(d)}>
                  {d}
                </button>
              ))}
              <button className="lx-key wide" onClick={() => press("C")}>
                ล้าง
              </button>
              <button className="lx-key" onClick={() => press("0")}>
                0
              </button>
              <button className="lx-key wide" onClick={() => press("DEL")}>
                ⌫
              </button>
            </div>
          </section>

          {/* โพย — เดสก์ท็อป: แผงด้านขวา · มือถือ: แถบสรุปลอย + sheet ตรวจสอบ */}
          <aside className="lx-card lx-slip vx-slipdesk">
            <div className="lx-spread" style={{ marginBottom: cart.length > 0 && slipOpen ? 10 : 0 }}>
              <div className="lx-row" style={{ gap: 8 }}>
                <Icon icon="solar:bill-list-bold-duotone" width={18} height={18} style={{ color: "var(--lx-accent-strong)" }} />
                <b>โพยของคุณ</b>
                {cart.length > 0 && <span className="lx-count-badge">{cart.length}</span>}
              </div>
              {cart.length > 0 && (
                <div className="lx-row" style={{ gap: 8 }}>
                  {slipOpen && (
                    <button className="lx-clear-btn" onClick={() => setCart([])}>
                      <Icon icon="solar:trash-bin-minimalistic-bold" width={13} height={13} />
                      ล้างโพย
                    </button>
                  )}
                  <button className="lx-slip-toggle" onClick={() => setSlipOpen((v) => !v)} aria-expanded={slipOpen}>
                    <Icon icon={slipOpen ? "solar:alt-arrow-down-bold" : "solar:alt-arrow-up-bold"} width={14} height={14} />
                    {slipOpen ? "ย่อ" : "ขยาย"}
                  </button>
                </div>
              )}
            </div>

            {cart.length === 0 ? (
              <Empty icon="solar:cursor-bold-duotone">แตะตัวเลขเพื่อเพิ่มลงโพย</Empty>
            ) : slipOpen ? (
              <>
                {slipRows}
                {checkout}
              </>
            ) : null}
          </aside>

          <div className="vx-bar">
            <div className="vx-bar-sum">
              <span className="vx-bar-count">{cart.length}</span>
              <div>
                <div className="vx-bar-lbl">{cart.length ? "โพยของคุณ" : "ยังไม่มีเลขในโพย"}</div>
                <b className="lx-mono vx-bar-total">{money2(total)} บาท</b>
              </div>
            </div>
            <button className="lx-btn primary vx-bar-go" disabled={!cart.length} onClick={() => setSheetOpen(true)}>
              ตรวจสอบโพย
              <Icon icon="solar:alt-arrow-up-bold" width={16} height={16} />
            </button>
          </div>

          <Sheet
            open={sheetOpen && cart.length > 0}
            onClose={() => setSheetOpen(false)}
            title={`โพยของคุณ (${cart.length})`}
            footer={checkout}
          >
            <div className="lx-spread" style={{ marginBottom: 6 }}>
              <span className="lx-muted" style={{ fontSize: 12 }}>
                ตรวจเลขและแก้ยอดได้ก่อนยืนยัน
              </span>
              <button className="lx-clear-btn" onClick={() => setCart([])}>
                <Icon icon="solar:trash-bin-minimalistic-bold" width={13} height={13} />
                ล้างโพย
              </button>
            </div>
            {slipRows}
          </Sheet>
        </>
      )}
    </div>
  );
}
